import React from 'react';
import Button from './Button';
import './StatusBanner.css';

const SUPPORTED_KINDS = ['error', 'success', 'warning', 'loading', 'empty', 'info'];

/**
 * Generic status banner primitive.
 * Renders inline feedback (loading, error, success, etc.) with an optional retry action.
 *
 * @param {object} props
 * @param {'info'|'error'|'success'|'warning'|'loading'|'empty'} [props.kind='info']
 * @param {React.ReactNode} props.message - Banner copy: a string, or richer
 *   JSX (e.g. a heading plus paragraphs) for callers that need more than
 *   plain text. Renders nothing when falsy.
 * @param {() => void} [props.onRetry] - Optional retry handler. Only shown when kind === 'error'.
 * @param {string} [props.retryLabel='Retry'] - Button label for the retry action.
 * @param {string} [props.className] - Extra class names appended to the banner.
 * @param {boolean} [props.live=false] - When true, the banner is announced as
 *   a polite live region (role="status" aria-live="polite") so screen readers
 *   pick up transient feedback (e.g. "Link copied"). Defaults to off so
 *   persistent banners do not interrupt the user.
 */
function StatusBanner({
  kind = 'info',
  message,
  onRetry,
  retryLabel = 'Retry',
  className = '',
  live = false,
}) {
  if (!message) {
    return null;
  }

  const normalizedKind = SUPPORTED_KINDS.includes(kind) ? kind : 'info';
  const classes = [
    'director-inline-banner',
    `director-inline-banner-${normalizedKind}`,
    className
  ].filter(Boolean).join(' ');

  const showRetry = normalizedKind === 'error' && typeof onRetry === 'function';

  const liveProps = live ? { role: 'status', 'aria-live': 'polite' } : {};

  return (
    <div className={classes} {...liveProps}>
      <span className="director-inline-banner-message">{message}</span>
      {showRetry ? (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

export default StatusBanner;
